import {Box, Button, Dialog, DialogActions, DialogContent, useTheme} from "@mui/material";
import {useContext, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import BootstrapDialogTitle from "../../../common/components/BootstrapDialogTitle";
import CustomStepper from "../../../common/components/CustomStepper";
import {initialPeakCreation, peakCreationAtom} from "../../../state/peaksRecoil";
import PeakAssociation from "./ASSOCIATION/PeakAssociation";
import PeakBaseDefinition from "./DEFINITION/PeakBaseDefinition";
import PeakMatrixDefinition from "./MATRIX/PeakMatrixDefinition";
import {BASE_DEFINITION_FORM_STEPS} from "../../../constants";
import * as yup from "yup";
import {FormProvider, useForm} from "react-hook-form";
import {FormPeakAssociationModel, FormPeakModel} from "../../../api/types/peak";
import {yupResolver} from "@hookform/resolvers/yup";

type PeakCreationFormProps = {
    isOpen: boolean;
    onDone: () => void;
    onClose: () => void;
    onStepChange?: (index: number) => void;
    refresh: Function;
};

const PeakFormContainer = ({isOpen, onClose, onStepChange, onDone, refresh}: PeakCreationFormProps) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // use Theme
    const theme = useTheme();

    // Use Recoil
    const [peakCreation, setPeakCreation] = useRecoilState(peakCreationAtom);
    const {readOnly, completed, step, definitionData} = peakCreation;

    // Use State
    const [initialValues] = useState<FormPeakModel>({
        commercialCategory: null,
        commercialSegmentId: null,
        name: "",
        from: "",
        to: "",
        description: "",
        dealer: null,
        legalEntity: null
    });

    const [initialValuesAssociation] = useState<FormPeakAssociationModel>({
        region: null,
        locations: []
    });

    const submitRef = useRef<HTMLButtonElement>(null);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    const currentStep = () => {
        switch (step) {
            case BASE_DEFINITION_FORM_STEPS.DEFINITION:
                return <PeakBaseDefinition submitRef={submitRef} refresh={refresh}/>;
            case BASE_DEFINITION_FORM_STEPS.ASSOCIATION:
                return <FormProvider {...formAssociation}><PeakAssociation submitRef={submitRef}
                                                                           refresh={refresh}/></FormProvider>;
            case BASE_DEFINITION_FORM_STEPS.MATRIX:
                return <PeakMatrixDefinition submitRef={submitRef}/>;
            case BASE_DEFINITION_FORM_STEPS.DONE:
                onDone();
                setPeakCreation(initialPeakCreation);
                return;
            default:
                return null;
        }
    };

    const validationSchema = yup
        .object()
        .shape({
            name: yup.string().required(t("translation:validation.fieldMandatory")).min(2),
            commercialCategory: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string()
                })
                .required(t("translation:validation.fieldMandatory"))
                .nullable(true),
            commercialSegmentId: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string()
                })
                .required(t("translation:validation.fieldMandatory"))
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            description: yup.string().required(t("translation:validation.fieldMandatory")),
            from: yup
                .string()
                .required(t("translation:validation.fieldMandatory"))
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            to: yup
                .string()
                .required(t("translation:validation.fieldMandatory"))
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            dealer: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string()
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            legalEntity: yup
                .object()
                .shape({
                    code: yup.string(),
                    label: yup.string()
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
        })
        .defined();

    // Use Form Context
    const form = useForm<FormPeakModel>({
        defaultValues: definitionData || initialValues,
        resolver: yupResolver(
            validationSchema,
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: true
    });

    const validationSchemaAssociation = yup
        .object()
        .shape({
            locations: yup.array().of(
                yup.object().shape({
                    code: yup.string(),
                    label: yup.string()
                }))
                .min(1)
                .nullable()
                .required(t("translation:validation.fieldMandatory"))
        })
        .defined();

    // Use Form Context
    const formAssociation = useForm<FormPeakAssociationModel>({
        defaultValues: useMemo(() => initialValuesAssociation, [initialValuesAssociation]),
        resolver: yupResolver(
            validationSchemaAssociation,
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: false
    });

    const onSubmit = () => {
        submitRef?.current?.click();
    };

    const handleOnClose = () => {
        onClose();
        form.reset(initialValues)
        formAssociation.reset(initialValuesAssociation)
    };

    const saveAndQuit = async () => {
        if (step === 0) {
            const commercialCategory = await form.trigger("commercialCategory")
            const commercialSegmentId = await form.trigger("commercialSegmentId")
            const name = await form.trigger("name")
            const from = await form.trigger("from")
            const to = await form.trigger("to")
            const description = await form.trigger("description")
            if (commercialCategory && commercialSegmentId && name && from && to && description) {
                onSubmit();
                handleOnClose();
            }
        } else if (step === 1) {
            const locations = await formAssociation.trigger("locations")
            if (locations) {
                onSubmit();
                handleOnClose();
            }
        }
    };
    const handleChangeStep = (clickedStep: number) => {
        if (clickedStep <= step || completed.slice(step, clickedStep).every((value) => value === true)) {
            onStepChange && onStepChange(clickedStep);
            setPeakCreation({...peakCreation, step: clickedStep});
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("pricing:steps.warningNextStep"),
                        type: snackbarTypes.warning,
                        show: true,
                        autoClose: true
                    }
                }
            });
        }
    };

    return (
        <Dialog open={isOpen} maxWidth={"xl"} fullWidth>
            <BootstrapDialogTitle id="customized-dialog-title" onClose={handleOnClose}>
                <Box sx={{display: "flex", alignItems: "center", width: "100%"}}>
                    <CustomStepper
                        activeStep={step}
                        completed={completed}
                        steps={[
                            t("pricing:steps.definition"),
                            t("pricing:steps.association"),
                            t("pricing:steps.matrix")
                        ]}
                        handleStep={handleChangeStep}
                    />
                </Box>
            </BootstrapDialogTitle>
            <DialogContent style={{color: theme.palette.primary.main}}>
                <FormProvider {...form}>
                    {currentStep()}
                </FormProvider>
            </DialogContent>
            {!readOnly &&
                <DialogActions style={{backgroundColor: theme.palette.neutral.bright, padding: "1rem"}}>
                    <Button variant="outlined" color={"accent"} onClick={handleOnClose}>
                        {t("pricing:actions.cancel")}
                    </Button>
                    {step === BASE_DEFINITION_FORM_STEPS.MATRIX || (
                        <Button variant="outlined" color={"accent"} onClick={saveAndQuit}>
                            {t("pricing:actions.saveAndExit")}
                        </Button>
                    )}
                    <Button
                        sx={{marginLeft: "16px"}}
                        color={"accent"}
                        onClick={onSubmit}
                        onSubmit={onSubmit}
                    >
                        {step === BASE_DEFINITION_FORM_STEPS.MATRIX
                            ? t("pricing:actions.saveAndExit")
                            : t("pricing:actions.saveAndProceed")}
                    </Button>
                </DialogActions>
            }
        </Dialog>
    );
};

export default PeakFormContainer;
